import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AbstractAssociationService } from '@abstract-services/association/1.0/association.abstract-service';
import { AssociationAPI } from '@constants/api';
import { Association, AssociationResponse } from '@interfaces/association/1.0';

@Injectable({
  providedIn: 'root',
})
export class AssociationService extends AbstractAssociationService {
  assocApiUrl: string;
  assocForCatalogEntryApiUrl: string;
  mappedAssocForCatalogEntryApiUrl: string;

  constructor(private http: HttpClient) {
    super();
    this.assocApiUrl = AssociationAPI.GET;
    this.assocForCatalogEntryApiUrl = AssociationAPI.GET_FOR_CATALOG_ENTRY;
    this.mappedAssocForCatalogEntryApiUrl = AssociationAPI.GET_MAPPED_FOR_CATALOG_ENTRY;
  }

  private normalizeRawPosValue(rawPosValue: unknown): string {
    if (rawPosValue == null) {
      return '';
    }

    return String(rawPosValue).trim();
  }

  /**
   * create
   *
   * @param association [Association] single association
   * @returns observable of current single association
   */
  create(association: Association): Observable<Association> {
    const BODY = {
      ...association,
      rawPosValue: this.normalizeRawPosValue(association?.rawPosValue),
    };
    const url = this.assocApiUrl.replace(':dataGroupId', association.dataGroupId);
    return this.http
      .post<Association>(url, BODY)
      .pipe(catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * get
   *
   * @param dataGroupIds partial<dataGroups>
   * @returns observable of all associations for the dataGroupId for the brand
   */
  get(dataGroupId: string): Observable<Association> {
    const url = this.assocApiUrl.replace(':dataGroupId', dataGroupId);
    return this.http
      .get<Association>(url)
      .pipe(catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /**
   * delete
   *
   * @param association [Association]
   * @returns observable of unknown type
   */
  delete(association: Association): Observable<any> {
    const url = this.assocApiUrl.replace(':dataGroupId', association.dataGroupId);
    const normalizedBody = {
      ...association,
      rawPosValue: this.normalizeRawPosValue(association?.rawPosValue),
    };

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: normalizedBody,
    };
    return this.http
      .delete<any>(url, options)
      .pipe(catchError((err: HttpErrorResponse) => throwError(err)));
  }

  getForCatalogEntry(catalogEntryId: string): Observable<Association[]> {
    const url = this.assocForCatalogEntryApiUrl.replace(':catalogEntryId', catalogEntryId);
    return this.http.get<AssociationResponse>(url).pipe(
      map((response: AssociationResponse) => {
        if (response && response._embedded) {
          return response._embedded.associations;
        } else {
          return undefined;
        }
      }),
      catchError((err: HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * getMappedForCatalogEntry - gets all associations for a given catalogEntryId
   *
   * @param catalogEntryId [string]
   * @returns Observable association list
   */
  getMappedForCatalogEntry(catalogEntryId: string): Observable<Association[]> {
    const url = this.mappedAssocForCatalogEntryApiUrl.replace(':catalogEntryId', catalogEntryId);
    return this.http.get<AssociationResponse>(url).pipe(
      map((response: AssociationResponse) => {
        if (response && response._embedded) {
          return response._embedded.associations;
        } else {
          return undefined;
        }
      }),
      catchError((err: HttpErrorResponse) => throwError(err)),
    );
  }
}
